import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

import { useSetFocus } from '@please/lrud'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { initFavorites } from '@/actions/actions'
import { Button } from '@/components/Button/Button'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { List } from '@/components/List/List'
import { Loader } from '@/components/Loader'
import { PageContentLoaderWrapper } from '@/components/PageContent/styles'
import { PageHeader } from '@/components/PageHeader/PageHeader'
import { LineTileShape } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { LoginSessionType } from '@/constants/login'
import { popupStatus } from '@/constants/popup'
import { scrollOptions } from '@/constants/scrollers'
import { PageContainer } from '@/containers/Search/styles'
import { ButtonDescription } from '@/containers/Subscriptions/styles'
import useStore from '@/store/useStore'
import { TileType, tileTypes } from '@/types/servicesTypes/configTypes'
import { SlideDataType } from '@/types/sliderTypes'

import {
  ContentUnavailableWrapper,
  FavoritesPlaceholderWrapper,
  LoginButtonWrapper,
} from './styles'

export const FavoritesRaw = () => {
  const { t } = useTranslation()
  const { isRegisteredUser } = useAuth()
  const setFocus = useSetFocus()

  const setAuthPopup = useStore((state) => state.common.setAuthPopup)
  const setPage = useStore((state) => state.page.setPage)
  const pagePopupStatus = useStore((state) => state.popup.pagePopupStatus)
  const activeTileId = useStore((state) => state.page.page?.focusId)
  const pageFocusId = `${focusIds.FAVORITES}${focusIds.LIST}${focusIds.CONTAINER_MOD}`
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED

  const [favoritePages, setFavoritePages] =
    useState<Nullable<SlideDataType[]>>(null)
  const [isLoading, setIsLoading] = useState(false)

  const favoritePagesLength = favoritePages?.length
  const activeTileIndex =
    favoritePages?.findIndex(({ id }) => id === activeTileId) || -1

  const listScrollOptions =
    scrollOptions.ListPageScroll[LineTileShape.ASPECT16x9]

  useLayoutEffect(() => {
    setPage({
      id: focusIds.FAVORITES,
      categoryId: focusIds.FAVORITES,
      name: focusIds.FAVORITES,
      title: focusIds.FAVORITES,
    })
    setFocus(pageFocusId)
    return () => {
      setPage(null)
    }
  }, [])

  useEffect(() => {
    if (isPagePopupOpen || !isRegisteredUser) {
      return
    }
    initFavoritePages()
  }, [isPagePopupOpen, isRegisteredUser])

  const initFavoritePages = async () => {
    setIsLoading(true)
    const favorites = await initFavorites()

    const favoritesPages =
      favorites &&
      Object.values(favorites).map((page) => {
        return {
          id: page.id,
          title: page.title,
          shade: true,
          showTitle: true,
          type: tileTypes.PAGE,
          data: { pageId: page.id, type: tileTypes.PAGE },
          previewImage: page.previewImage,
          image: page.icon,
        }
      })

    setFavoritePages(favoritesPages)
    setIsLoading(false)
  }

  const fetchData = useCallback(
    async (fetchLimit: number, next: string) => {
      if (!favoritePages) {
        return
      }
      const start = next ? Number(next) * fetchLimit : 0
      const lineData = favoritePages.slice(start, start + fetchLimit)

      return {
        items: lineData,
        totalCount: favoritePagesLength,
        next: favoritePagesLength || 0 > lineData.length ? next + 1 : '',
      } as PaginatedData<TileType>
    },
    [favoritePages],
  )

  const registeredText =
    favoritePages === null
      ? t('toasts.favoriteIsUnavailable')
      : t('emptyFavorites')
  const text = isRegisteredUser ? registeredText : t('favoritesUnavailable')

  const goToLogin = () => {
    return setAuthPopup({
      sessionType: LoginSessionType.LOGIN,
      prevFocusId: pageFocusId,
    })
  }

  return (
    <PageContainer
      focusId={pageFocusId}
      elementType="div"
      className="content-wrapper"
    >
      <PageHeader name={t('FAVORITES')} />
      {!isLoading ? (
        isRegisteredUser && favoritePagesLength ? (
          <List
            pageFocusId={pageFocusId}
            listContextId={focusIds.FAVORITES}
            activeTileIndex={activeTileIndex > -1 ? activeTileIndex : 0}
            activeTileId={activeTileId}
            fetchData={fetchData}
            scrollOptions={listScrollOptions}
            isPage
          />
        ) : (
          <FavoritesPlaceholderWrapper>
            <ContentUnavailableWrapper>
              <ContentUnavailable>{text}</ContentUnavailable>
            </ContentUnavailableWrapper>
            {!isRegisteredUser && (
              <LoginButtonWrapper>
                <ButtonDescription>{t('haveAccount')}</ButtonDescription>
                <Button
                  iconSrc={icons.register.loginIcon}
                  text={t('login')}
                  onClick={goToLogin}
                  width={340}
                />
              </LoginButtonWrapper>
            )}
          </FavoritesPlaceholderWrapper>
        )
      ) : (
        <PageContentLoaderWrapper>
          <Loader />
        </PageContentLoaderWrapper>
      )}
    </PageContainer>
  )
}

export const Favorites = memo(FavoritesRaw)
